import { useParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { api } from "@/lib/api"
import type { PaperDetail } from "@/types/api"
import { formatDate } from "@/lib/utils"
import { BookmarkButton } from "@/components/papers/BookmarkButton"
import { FeedbackBar } from "@/components/papers/FeedbackBar"

export default function PaperDetailPage() {
  const { id } = useParams()
  const { data, isLoading, error } = useQuery({
    queryKey: ["paper", id],
    queryFn: () => api.get<PaperDetail>(`/papers/${id}`),
    enabled: !!id,
  })

  if (isLoading) return <div className="text-sm text-muted-foreground">불러오는 중...</div>
  if (error) return <div className="text-sm text-destructive">{(error as Error).message}</div>
  if (!data) return null

  const title = data.summary?.title_ko || data.title_en

  return (
    <article className="space-y-6">
      <header>
        <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>{formatDate(data.published_at)}</span>
          {data.arxiv_id && <span>arXiv:{data.arxiv_id}</span>}
          {data.tags.slice(0, 5).map((t) => (
            <span key={t.tag} className="rounded bg-secondary px-2 py-0.5">
              {t.tag}
            </span>
          ))}
        </div>
        <div className="mb-1 flex items-start gap-2">
          <h1 className="flex-1 text-2xl font-semibold leading-tight">{title}</h1>
          <BookmarkButton paperId={data.id} size="md" />
        </div>
        <p className="text-sm text-muted-foreground">{data.title_en}</p>
        <div className="mt-2 text-xs text-muted-foreground">
          {data.authors.map((a) => a.name).join(", ")}
        </div>
        <div className="mt-3 flex gap-3 text-sm">
          {data.paper_url && (
            <a href={data.paper_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">
              원문 →
            </a>
          )}
          {data.pdf_url && (
            <a href={data.pdf_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">
              PDF
            </a>
          )}
          {data.code_url && (
            <a href={data.code_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">
              code
            </a>
          )}
        </div>
      </header>

      {data.summary && (
        <>
          {data.summary.tldr_ko && (
            <section>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">한 줄 요약</h3>
              <p className="text-base">{data.summary.tldr_ko}</p>
            </section>
          )}
          {data.summary.summary_3line && (
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">3줄 요약</h3>
              <div className="whitespace-pre-line rounded-md border-l-4 border-border bg-secondary/30 p-4 text-sm">
                {data.summary.summary_3line}
              </div>
            </section>
          )}
          {data.summary.summary_long && (
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">상세 요약</h3>
              <p className="text-sm leading-7 text-foreground/90">{data.summary.summary_long}</p>
            </section>
          )}
          {data.summary.key_contributions.length > 0 && (
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">핵심 기여</h3>
              <ul className="list-disc space-y-1 pl-5 text-sm">
                {data.summary.key_contributions.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </section>
          )}
          {data.summary.why_important && (
            <section>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">왜 중요한가</h3>
              <p className="text-sm leading-6">{data.summary.why_important}</p>
            </section>
          )}
          {data.summary.use_cases.length > 0 && (
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">실무 활용</h3>
              <ul className="list-disc space-y-1 pl-5 text-sm">
                {data.summary.use_cases.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </section>
          )}
          {data.summary.limitations && (
            <section>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">한계 / 주의</h3>
              <p className="text-sm leading-6">{data.summary.limitations}</p>
            </section>
          )}
          <p className="text-[10px] text-muted-foreground">
            {data.summary.model_used} · {data.summary.prompt_version} · {formatDate(data.summary.generated_at)}
          </p>
        </>
      )}

      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">초록 (원문)</h3>
        <p className="whitespace-pre-line text-sm leading-7 text-foreground/80">{data.abstract_en}</p>
      </section>

      <FeedbackBar paperId={data.id} />
    </article>
  )
}
